import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import CEODashboard from "@/components/dashboard/CEODashboard";
import GMDashboard from "@/components/dashboard/GMDashboard";
import StaffDashboard from "@/components/dashboard/StaffDashboard";
import SalesDashboard from "@/components/dashboard/SalesDashboard";

type CeoDashboardView = "ceo" | "manager";

const Dashboard = () => {
  const { user, userRole, loading } = useAuth();
  const navigate = useNavigate();
  const [ceoDashboardView, setCeoDashboardView] = useState<CeoDashboardView>("ceo");

  useEffect(() => {
    if (!loading && !user) {
      navigate("/auth");
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || !userRole) {
    return null;
  }

  const renderCeoDashboard = () => {
    const isManagerView = ceoDashboardView === "manager";
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-end gap-2">
          <Button
            variant={isManagerView ? "outline" : "default"}
            onClick={() => setCeoDashboardView("ceo")}
          >
            CEO Dashboard
          </Button>
          <Button
            variant={isManagerView ? "default" : "outline"}
            onClick={() => setCeoDashboardView("manager")}
          >
            Manager Dashboard
          </Button>
        </div>
        {isManagerView ? <GMDashboard /> : <CEODashboard />}
      </div>
    );
  };

  const renderDashboard = () => {
    switch (userRole) {
      case "ceo":
        return renderCeoDashboard();
      case "general_manager":
        return <GMDashboard />;
      case "sales_staff":
        return <SalesDashboard />;
      case "operations_staff":
      case "finance_staff":
        return <StaffDashboard />;
      default:
        return (
          <div className="flex items-center justify-center p-8">
            <p className="text-muted-foreground">No role assigned. Please contact an administrator.</p>
          </div>
        );
    }
  };

  return (
    <DashboardLayout>
      {renderDashboard()}
    </DashboardLayout>
  );
};

export default Dashboard;
